import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Minus, Plus, Check, Truck, Leaf, ShieldCheck, ChevronLeft } from "lucide-react";
import { PRODUCTS } from "../mock";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import ProductReviews from "../components/ProductReviews";
import { toast } from "sonner";
import { fetchProduct, fetchProducts, requestRestock } from "../api";

const ProductDetail = () => {
  const { slug } = useParams();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(() => PRODUCTS.find((p) => p.slug === slug));
  const [allProducts, setAllProducts] = useState(PRODUCTS);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState("benefits");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySent, setNotifySent] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [reviewStats, setReviewStats] = useState({ count: 0, average: 0 });

  const displayRating = reviewStats.count > 0 ? reviewStats.average : product?.rating;
  const displayCount = reviewStats.count > 0 ? reviewStats.count : product?.reviews;

  const handleNotify = async (e) => {
    e.preventDefault();
    const email = notifyEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setNotifyLoading(true);
    try {
      const res = await requestRestock(product.slug, email);
      setNotifySent(true);
      toast.success("You're on the list", { description: res?.message || "We'll email you when it's back." });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setNotifyLoading(false);
    }
  };

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    setNotifySent(false);
    setNotifyEmail("");
    setProduct(PRODUCTS.find((p) => p.slug === slug));
    fetchProduct(slug)
      .then((data) => data && setProduct(data))
      .catch(() => {});
    fetchProducts()
      .then((data) => {
        if (Array.isArray(data) && data.length) setAllProducts(data);
      })
      .catch(() => {});
  }, [slug]);

  if (!product) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 py-40 text-center">
        <h2 className="font-display text-4xl text-[#f4efe4] mb-6">Product not found</h2>
        <Link to="/shop" className="btn-outline-gold inline-flex px-8 py-3 rounded-full text-sm tracking-[0.15em] uppercase">
          Back to Shop
        </Link>
      </div>
    );
  }

  const related = allProducts.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 4);
  const fallback = allProducts.filter((p) => p.id !== product.id).slice(0, 4);
  const suggestions = related.length >= 2 ? related : fallback;

  const handleAdd = () => {
    addItem(product, qty);
    toast.success("Added to cart", { description: `${qty} × ${product.name}` });
  };

  const tabs = [
    { id: "benefits", label: "Benefits" },
    { id: "ingredients", label: "Ingredients" },
    { id: "how", label: "How to Use" }
  ];

  return (
    <div>
      <div className="max-w-[1280px] mx-auto px-6 pt-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-[#a29a8c] hover:text-[#c9a24b] text-sm transition-colors">
          <ChevronLeft size={16} /> Back to Collection
        </Link>
      </div>

      <section className="max-w-[1280px] mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
        {/* Gallery */}
        <div>
          <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-[#2c2822] bg-gradient-to-b from-[#f6f1e7] to-[#e4dccb]">
            <img src={product.gallery[activeImg]} alt={product.name} className="w-full h-full object-contain p-8" />
            {product.badge && (
              <span className="absolute top-5 left-5 bg-[#c9a24b] text-[#0b0a09] text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-full font-medium">
                {product.badge}
              </span>
            )}
          </div>
          {product.gallery.length > 1 && (
            <div className="flex gap-4 mt-4">
              {product.gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-24 rounded-md overflow-hidden border transition-colors bg-gradient-to-b from-[#f6f1e7] to-[#e4dccb] ${
                    activeImg === i ? "border-[#c9a24b]" : "border-[#2c2822] hover:border-[#c9a24b]/50"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain p-1.5" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="text-[#c9a24b] text-xs tracking-[0.3em] uppercase mb-4">{product.category}</p>
          <h1 className="font-display text-[40px] md:text-[52px] text-[#f4efe4] leading-[1.05]">
            {product.name}
          </h1>
          <p className="text-[#a29a8c] mt-3">{product.tagline}</p>

          <div className="flex items-center gap-2 mt-5">
            {Array.from({ length: 5 }).map((_, j) => (
              <Star key={j} size={16} className={j < Math.round(displayRating) ? "fill-[#c9a24b] text-[#c9a24b]" : "text-[#3a342b]"} />
            ))}
            <span className="text-[#a29a8c] text-sm ml-1">{Number(displayRating).toFixed(1)} · {displayCount} reviews</span>
          </div>

          <div className="flex items-center gap-3 mt-6">
            <span className="font-display text-4xl text-[#c9a24b]">${product.price.toFixed(2)}</span>
            {product.compareAt && (
              <span className="text-[#6b6459] text-lg line-through">${product.compareAt.toFixed(2)}</span>
            )}
          </div>

          <p className="text-[#c8c0b2] leading-relaxed mt-6">{product.description}</p>

          <div className="gold-hairline my-8" />

          {product.inStock ? (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-[#2c2822] rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-12 flex items-center justify-center text-[#f4efe4] hover:text-[#c9a24b] transition-colors">
                  <Minus size={16} />
                </button>
                <span className="w-10 text-center text-[#f4efe4]">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="w-11 h-12 flex items-center justify-center text-[#f4efe4] hover:text-[#c9a24b] transition-colors">
                  <Plus size={16} />
                </button>
              </div>
              <button onClick={handleAdd} className="btn-gold flex-1 min-w-[200px] py-4 rounded-full text-sm tracking-[0.2em] uppercase font-medium">
                Add to Cart
              </button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="restock-block">
              <button disabled className="w-full py-4 rounded-full text-sm tracking-[0.2em] uppercase bg-[#1b1815] text-[#6b6459] border border-[#2c2822] cursor-not-allowed" data-testid="sold-out-btn">
                Sold Out
              </button>
              {notifySent ? (
                <div className="flex items-center gap-3 border border-[#c9a24b]/40 rounded-xl px-5 py-4 text-[#c9a24b]" data-testid="restock-confirmation">
                  <Check size={18} className="shrink-0" />
                  <span className="text-sm">You're on the list — we'll email you the moment it's back.</span>
                </div>
              ) : (
                <form onSubmit={handleNotify} className="space-y-3" data-testid="restock-form">
                  <p className="text-[#a29a8c] text-sm">Enter your email and we'll notify you when this restocks.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="you@email.com"
                      required
                      data-testid="restock-email-input"
                      className="flex-1 bg-[#0b0a09] border border-[#2c2822] rounded-full px-5 py-4 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={notifyLoading}
                      data-testid="restock-submit-btn"
                      className="btn-outline-gold px-8 py-4 rounded-full text-sm tracking-[0.18em] uppercase whitespace-nowrap disabled:opacity-60"
                    >
                      {notifyLoading ? "Adding…" : "Notify Me"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mt-9">
            {[{ icon: Leaf, t: "100% Natural" }, { icon: Truck, t: "Free over $60" }, { icon: ShieldCheck, t: "Cruelty Free" }].map((f, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2 border border-[#2c2822] rounded-lg py-5">
                <f.icon size={22} className="text-[#c9a24b]" strokeWidth={1.4} />
                <span className="text-[#a29a8c] text-xs">{f.t}</span>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="mt-10">
            <div className="flex gap-6 border-b border-[#2c2822]">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`pb-3 text-sm tracking-[0.12em] uppercase transition-colors relative ${
                    tab === t.id ? "text-[#c9a24b]" : "text-[#a29a8c] hover:text-[#f4efe4]"
                  }`}
                >
                  {t.label}
                  {tab === t.id && <span className="absolute left-0 -bottom-px w-full h-px bg-[#c9a24b]" />}
                </button>
              ))}
            </div>
            <div className="pt-6 text-[#c8c0b2] leading-relaxed">
              {tab === "benefits" && (
                <ul className="space-y-3">
                  {product.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-[#c9a24b] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {tab === "ingredients" && <p>{product.ingredients}</p>}
              {tab === "how" && <p>{product.howToUse}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <ProductReviews slug={product.slug} onStats={setReviewStats} />

      {/* Related */}
      <section className="max-w-[1280px] mx-auto px-6 py-20 border-t border-[#2c2822]">
        <h2 className="font-display text-[34px] md:text-[44px] text-[#f4efe4] mb-10">You may also love</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {suggestions.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
